import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlatformDemo = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 bg-[#0F0F11]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FFD100]/10 border border-[#FFD100]/20 rounded-full px-4 py-2 mb-6">
            <Icon name="Layout" size={16} color="#FFD100" />
            <span className="text-[#FFD100] text-sm font-semibold">PLATFORM DEMO</span>
          </div>
          <h2 className="text-5xl font-bold text-white mb-6">
            Experience <span className="text-[#FFD100]">Bee Wise OS</span><br />
            In Action
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-8">
            See how enterprise teams transform onboarding chaos into systematic success
          </p>
          <Button
            variant="default"
            size="xl"
            onClick={() => navigate('/main-dashboard')}
            className="bg-[#FFD100] hover:bg-[#FFD100]/90 text-[#0B1220] font-semibold px-12 py-4 text-lg shadow-lg shadow-[#FFD100]/20">
            Launch Interactive Demo
          </Button>
        </div>

        {/* Platform Screenshot Mockup */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
          <div className="bg-[#0B1220] p-4 border-b border-white/10 flex items-center gap-2">
            <div className="flex gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
            <div className="flex-1 text-center">
              <span className="text-white/60 text-sm">firstondate.com/dashboard</span>
            </div>
          </div>
          
          <div className="p-8 bg-gradient-to-br from-[#0B1220] to-[#1A2332]">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar Preview */}
              <div className="space-y-3">
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-[#FFD100]/10 hover:border-[#FFD100]/40 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Icon name="Home" size={20} color="#FFD100" />
                    <span className="text-white font-medium">Start Here</span>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Icon name="BookOpen" size={20} color="rgba(255,255,255,0.6)" />
                    <span className="text-white/70">Role Playbook</span>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Icon name="Heart" size={20} color="rgba(255,255,255,0.6)" />
                    <span className="text-white/70">Culture & Rituals</span>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Icon name="Settings" size={20} color="rgba(255,255,255,0.6)" />
                    <span className="text-white/70">Tools & Access</span>
                  </div>
                </div>
              </div>

              {/* Dashboard Content Preview */}
              <div className="lg:col-span-3 space-y-6">
                {/* Welcome Banner */}
                <div className="backdrop-blur-sm bg-gradient-to-r from-[#FFD100]/20 to-[#FFD100]/5 border border-[#FFD100]/30 rounded-2xl p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Good morning, Marko! 🔥</h3>
                  <p className="text-white/70 mb-4">You're on a 3-day streak. Keep it going!</p>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 bg-white/10 rounded-full h-3 overflow-hidden">
                      <div className="bg-[#FFD100] h-full" style={{ width: '65%' }}></div>
                    </div>
                    <span className="text-[#FFD100] font-bold">Level 2</span>
                  </div>
                </div>

                {/* Smart Checklist */}
                <div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                    <Icon name="CheckSquare" size={20} color="#FFD100" />
                    Your Smart Checklist
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-white/70">
                      <div className="w-5 h-5 bg-emerald-500 rounded flex items-center justify-center">
                        <Icon name="Check" size={14} color="white" strokeWidth={3} />
                      </div>
                      <span>Complete profile setup</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/70">
                      <div className="w-5 h-5 bg-emerald-500 rounded flex items-center justify-center">
                        <Icon name="Check" size={14} color="white" strokeWidth={3} />
                      </div>
                      <span>Read company handbook</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded border-2 border-[#FFD100]"></div>
                      <span>Request IT equipment access</span>
                    </div>
                  </div>
                </div>

                {/* Badge Showcase */}
                <div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                    <Icon name="Award" size={20} color="#FFD100" />
                    Your Achievements
                  </h4>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#FFD100]/20 rounded-lg flex items-center justify-center">
                      <Icon name="Star" size={20} color="#FFD100" />
                    </div>
                    <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center opacity-40">
                      <Icon name="Compass" size={20} color="rgba(255,255,255,0.4)" />
                    </div>
                    <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center opacity-40">
                      <Icon name="Zap" size={20} color="rgba(255,255,255,0.4)" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformDemo;